<template>
  <form @submit.prevent="handleSubmit">
    <div class="eu-display_flex eu-column eu-mb_5">
      <emd-text class="lable_title">
        {{ $t('form.title') }}
      </emd-text>
      <emd-text
        class="eu-mt_1"
        size="small"
        tag="p"
        variation-color="subtle"
      >
        {{ $t('form.subtle') }}
      </emd-text>
    </div>
    <emd-text class="lable_title">{{ $t('form.selection.scope') }} </emd-text>
    <div class="container eu-mt_4 eu-mb_5">
      <div
        v-for="(scope, index) in scopes"
        :key="index"
      >
        <div class="scope__container">
          <emd-switcher
            v-model="scope.all"
            color="primary"
            :label="$t(`form.${scope.scope}.title`)"
            @change="handleToggleAll(scope)"
          />
          <emd-text
            class="eu-mt_2"
            size="small"
            tag="p"
            variation-color="subtle"
          >
            {{ $t(`form.type.${scope.scope}.description`) }}
          </emd-text>
        </div>
        <fieldset
          :disabled="!scope.all"
          class="permissions__container"
        >
          <legend class="eu-mb_3">
            <emd-text
              size="small"
              class="_type_caption"
            >
              {{ $t('form.selection.permission') }}
            </emd-text>
          </legend>
          <label
            v-for="permission in scope.permissions"
            class="emd-radio-button"
            :key="permission.type"
            :for="permission.name + '-' + scope.scope"
          >
            <input
              v-model="permission.value"
              class="emd-radio-button__input _bold"
              type="radio"
              :value="true"
              :id="permission.name + '-' + scope.scope"
              :name="'permission' + '-' + scope.scope"
              @change="handleTogglePermission(scope, permission)"
            />
            <div class="emd-radio-button__area"></div>
            <span class="emd-radio-button__text emd-text _color_default">{{
              $t(`form.type.${permission.name}`)
            }}</span>
          </label>
        </fieldset>
      </div>
    </div>
    <hr class="emd-divider" />
    <password-confirmation
      class="eu-mt_5"
      label=""
      v-model="password"
      :has-error="authError"
    />
    <div class="create-key__footer">
      <emd-button
        color="primary"
        type="outline"
        data-testid="close-modal"
        @click="handleBack"
      >
        {{ $t('general.back') }}
      </emd-button>
      <emd-button
        color="primary"
        data-testid="create"
        type="submit"
        :disabled="computedDisabled"
      >
        <emd-loader v-if="isLoading"></emd-loader>
        <span v-else> {{ $t('form.button.create') }} </span>
      </emd-button>
    </div>
  </form>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import {
  EmdText,
  EmdButton,
  EmdSwitcher,
  EmdLoader
} from 'emerald-vue/src/components'

import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'

import CREATE_KEY_STEPS_ENUM from '../../enums/create-keys-steps.enum'

import {
  TRANSACTIONAL,
  FINANCIAL,
  RECIPIENTS
} from '../../enums/secret-keys-scopes.enum'

const SCOPES = [
  {
    scope: 'transaction',
    all: false,
    permissions: [
      { name: 'read-only', value: false, type: TRANSACTIONAL.r },
      { name: 'read-and-write', value: false, type: TRANSACTIONAL.rw }
    ]
  },
  {
    scope: 'financial',
    all: false,
    permissions: [
      { name: 'read-only', value: false, type: FINANCIAL.r },
      { name: 'read-and-write', value: false, type: FINANCIAL.rw }
    ]
  },
  {
    scope: 'recipients',
    all: false,
    permissions: [
      { name: 'read-only', value: false, type: RECIPIENTS.r },
      { name: 'read-and-write', value: false, type: RECIPIENTS.rw }
    ]
  }
]

export default {
  name: 'CustomKey',
  components: {
    EmdText,
    EmdButton,
    EmdSwitcher,
    EmdLoader,
    PasswordConfirmation
  },
  props: {
    authError: Boolean,
    isLoading: Boolean
  },

  data () {
    return {
      password: '',
      scopes: []
    }
  },

  validations: {
    password: {
      required
    },
    scopes: {
      $each: {
        all: {
          required
        },
        permissions: {
          anyAs: (values, vm) => {
            if (!vm.all) return true
            return values?.some(permission => permission.value)
          }
        }
      }
    }
  },

  computed: {
    computedDisabled () {
      return (
        this.$v.password.$invalid ||
        this.isLoading ||
        this.$v.scopes.$invalid ||
        !this.scopes.some(s => s.all)
      )
    }
  },

  created () {
    this.scopes = JSON.parse(JSON.stringify(SCOPES))
  },

  methods: {
    handleToggleAll (scope) {
      if (scope.all) return
      scope.permissions.forEach(permission => (permission.value = false))
      this.$v.scopes.$touch()
    },

    handleTogglePermission (scope, currentPermission) {
      if (!scope.all) return
      scope.permissions.forEach(p => {
        if (p.name === currentPermission.name) return
        p.value = false
      })
    },

    handleBack () {
      this.$emit('back', CREATE_KEY_STEPS_ENUM.create)
    },

    handleSubmit () {
      const filteredPermisions = this.scopes.flatMap(scope =>
        scope.all
          ? scope.permissions
              .filter(permission => permission.value)
              .map(permission => permission.type)
          : []
      )
      this.$emit('submit', {
        password: this.password,
        scope: filteredPermisions
      })
    }
  }
}
</script>

<style scoped>
.container {
  display: grid;
  grid-template-columns: repeat(auto-fill, 163px);
  justify-content: space-around;
  gap: 24px 0;
}
.scope__container {
  display: grid;
  grid-template-rows: 24px 62px;
}

.permissions__container {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lable_title {
  font-size: 14px;
  font-weight: 600px;
}

.create-key__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

@media only screen and (min-width: 640px) {
  .container {
    grid-template-columns: repeat(3, 163px);
    gap: 0;
  }
}
</style>

<i18n>
  {
      "pt-BR": {
          "form.title"  : "Personalizado",
          "form.subtle" : "Escolha os níveis entre Transacional, Financeiro e Recebedores.",
          "form.selection.scope"  : "Selecione os escopos:",
          "form.selection.permission"  : "Selecione uma permissão:",
          "form.transaction.title": "Transacional",
          "form.financial.title": "Financeiro",
          "form.recipients.title": "Recebedores",
          "form.type.read-only": "Somente leitura",
          "form.type.read-and-write": "Leitura e escrita",
          "form.type.transaction.description": "Gerencia pedidos, cobranças, assinaturas, faturas e relacionados.",
          "form.type.financial.description": "Gerencia operações financeiras, pagamentos e balanço.",
          "form.type.recipients.description": "Gerencia informações de recebedores.",
          "form.button.create": "Criar chave",
          "form.button.return": "Voltar"
      },
      "en-US": {
          "form.title": "Custom",
          "form.subtle": "Choose the levels between Transactional, Financial and Receivers.",
          "form.selection.scope":  "Select the scopes",
          "form.selection.permission"  : "Select a permission:",
          "form.transaction.title": "Transactional",
          "form.financial.title": "Financial",
          "form.recipients.title": "Receivers",
          "form.type.read-only": "Only reading",
          "form.type.read-and-write": "Reading and writing",
          "form.type.transaction.description": "Manage orders, billing, subscriptions, invoices and related items.",
          "form.type.financial.description": "Manages financial operations, payments and balance sheet.",
          "form.type.recipients.description": "Manages recipient information.",
          "form.button.create": "CreateKey",
          "form.button.return": "Return"
      }
  }
</i18n>
